import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function Login() {
  // Add navigation hook
  const navigate = useNavigate();
  
  // State variables to store form data and UI states
  const [credentials, setCredentials] = useState({
    usernameOrEmail: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Get auth context
  const { login, loading: authLoading, error: authError } = useAuth();

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update the credentials state with the new value
    setCredentials({
      ...credentials, // Keep all existing values
      [name]: value   // Update only the changed field
    });
  };

  // Handle checkbox change
  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the default form submission behavior
    
    // Validate form inputs
    if (!credentials.usernameOrEmail || !credentials.password) {
      setLocalError('Email/username and password are required');
      return;
    }
    
    try {
      setLocalLoading(true);
      setLocalError(null);
      setDebugInfo(null);
      
      console.log('🔐 Login: Attempting login with:', { 
        usernameOrEmail: credentials.usernameOrEmail, 
        password: '***',
        rememberMe
      });
      
      // Call the login function from AuthContext
      const result = await login(credentials, rememberMe);
      
      console.log('🔐 Login: Login result:', result);
      
      if (result && result.success) {
        // Handle successful login
        setSuccessMessage('Login successful! You will be redirected to the dashboard page in a moment.');
        
        // Get the current domain for multi-tenant setup
        const domain = window.location.hostname;
        
        // Determine the appropriate redirect path based on domain and user roles
        let redirectPath = '/dashboard'; // Default to dashboard which should be valid for all users
        
        // For admin domain, redirect to admin dashboard if user has superadmin role
        if (domain.includes('admin') && result?.user?.roles?.some(r => r.name === 'superadmin')) {
          console.log('🔐 Login: Admin domain + superadmin detected, redirecting to admin dashboard');
          redirectPath = '/admin'; 
        }
        
        console.log(`🔐 Login: Will redirect to: ${redirectPath} after delay`, {
          domain,
          userRoles: result?.user?.roles,
        });
        
        // Add a longer delay to ensure state updates before redirect
        setTimeout(() => {
          console.log(`🔐 Login: Now redirecting to: ${redirectPath}`);
          // Use navigate instead of window.location for better React integration
          navigate(redirectPath);
        }, 2000); // 2 seconds for better user experience
      } else {
        // Handle login failure
        console.error('🔐 Login: Login failed with result:', result);
        setLocalError((result && result.error) || 'Login failed. Please check your credentials.');
        setDebugInfo(result);
      }
      
    } catch (err) {
      // Handle unexpected errors
      console.error('🔐 Login: Login failed with exception:', err);
      setLocalError('An unexpected error occurred. Please check the console for details.');
      setDebugInfo({
        message: err.message,
        stack: err.stack,
        response: err.response ? {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        } : 'No response data'
      });
    } finally {
      setLocalLoading(false);
    }
  };

  // Determine if we're in a loading state
  const isLoading = localLoading || authLoading;
  
  // Show auth error if available, otherwise show local error
  const errorMessage = authError || localError;

  return (
    <div className="login-container">
      <div className="card">
        <div className="card-header">
          <h2>Login</h2>
        </div>
        <div className="card-body">
          {/* Show error message if there is one */}
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          
          {/* Show success message if there is one */}
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
          
          {/* Login form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="usernameOrEmail" className="form-label">Email or Username</label>
              <input
                type="text"
                className="form-control"
                id="usernameOrEmail"
                name="usernameOrEmail"
                value={credentials.usernameOrEmail}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
                checked={rememberMe}
                onChange={handleRememberMeChange}
                disabled={isLoading}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember me
              </label>
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          {/* Debug information */}
          {debugInfo && (
            <div className="mt-4">
              <h5>Debug Information</h5>
              <pre className="bg-light p-3 rounded text-small">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login; 